import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import debounce from 'debounce-promise'
import classNames from 'classnames'
import {
  Button,
  Card,
  Checkbox,
  Chip,
  Input,
  SelectAsync,
  Table,
} from '@arnsbogroup/react-voicearchive'
import messages from './messages'

const post = (url, body) => fetch(url, {
  method: 'POST',
  credentials: 'same-origin',
  body: JSON.stringify(body),
}).then((response) => response.json())

const loadCustomers = debounce((query) => fetch(`/rest/v2/customer?search[name]=${encodeURIComponent(query)}`)
  .then((response) => response.json())
  .then(({ data }) => data.map(({ uid, name, defaultcontact }) => ({ value: uid, label: name, defaultContact: defaultcontact }))), 250)

const loadSuppliers = debounce((query) => fetch(`/rest/v2/supplier?search[name]=${encodeURIComponent(query)}`)
  .then((response) => response.json())
  .then((suppliers) => suppliers.map(({ uid, firstname, lastname, userid }) => ({ value: uid, label: `${firstname} ${lastname} | ${userid}` }))), 250)

const toOption = (supplier) => supplier ? { value: supplier.uid, label: supplier.name } : null

/**
 * Preview rows become editable rows: duplicates and unknown languages start unticked
 */
const toEditable = (titles) => titles.map((title) => ({
  ...title,
  po: '',
  rows: title.rows.map((row) => ({
    ...row,
    include: !row.duplicateOf && !row.error,
    author: { ...row.author, selected: toOption(row.author.suggestions.find(({ uid }) => uid === row.author.supplierId)) },
    qc: { ...row.qc, selected: toOption(row.qc.suggestions.find(({ uid }) => uid === row.qc.supplierId)) },
  })),
}))

const Notice = ({ state = 'info', title, children }) => (
  <div className={classNames('border rounded px-4 py-3 text-sm space-y-1', {
    'border-warning bg-warning-lighter': state === 'warning',
    'border-negative bg-negative-lighter': state === 'negative',
    'border-positive bg-positive-lighter': state === 'positive',
    'border-gray-200 bg-gray-50': state === 'info',
  })}>
    { title && <div className="font-bold">{ title }</div> }
    { children }
  </div>
)

const CrewCell = ({ member, disabled, onChange }) => {
  const { formatMessage } = useIntl()

  if (!member.name && !member.cost && !member.selected) {
    return <Table.Td className="text-gray-400">–</Table.Td>
  }

  return (
    <Table.Td className="align-top">
      <div className="w-64 space-y-1">
        <div className="text-xs text-gray-500" title={member.source === 'alias' ? formatMessage(messages.remembered) : undefined}>
          { member.name || '—' }{ member.source === 'alias' && ' ✓' }
        </div>
        <SelectAsync
          size="sm"
          value={member.selected}
          isClearable
          isDisabled={disabled}
          defaultOptions={member.suggestions.map(toOption)}
          loadOptions={loadSuppliers}
          placeholder={formatMessage(messages.noSupplier)}
          onChange={(selected) => onChange({ ...member, selected })}
        />
        <Input
          type="number"
          step="0.01"
          min="0"
          className="w-28"
          aria-label={formatMessage(messages.cost)}
          placeholder={formatMessage(messages.cost)}
          disabled={disabled}
          value={member.cost ?? ''}
          onChange={({ target: { value } }) => onChange({ ...member, cost: value === '' ? null : Number(value) })}
        />
      </div>
    </Table.Td>
  )
}

const TitleCard = ({ title, onChange }) => {
  const { formatMessage, formatDate } = useIntl()

  const setRow = (index, changes) => onChange({
    ...title,
    rows: title.rows.map((row, i) => i === index ? { ...row, ...changes } : row),
  })

  return (
    <Card className="p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{ title.title }</h2>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <Chip size="xs">{ formatMessage(title.trailer ? messages.trailer : messages.fullTitle) }</Chip>
            { title.sheetRows && <span>{ formatMessage(messages.sheetRows, { rows: title.sheetRows }) }</span> }
            { title.existingOrders.map(({ uid, name }) => (
              <Link key={uid} to={`/orders/${uid}/overview`} className="text-primary" target="_blank">
                { formatMessage(messages.existingOrder) } #{ uid } ({ name })
              </Link>
            )) }
          </div>
        </div>
        <div className="w-56">
          <Input
            label={formatMessage(messages.po)}
            placeholder="PO-"
            value={title.po}
            onChange={({ target: { value } }) => onChange({ ...title, po: value })}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table.Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th>{ formatMessage(messages.language) }</Table.Th>
              <Table.Th>{ formatMessage(messages.due) }</Table.Th>
              <Table.Th>{ formatMessage(messages.status) }</Table.Th>
              <Table.Th>{ formatMessage(messages.author) }</Table.Th>
              <Table.Th>{ formatMessage(messages.qc) }</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            { title.rows.map((row, index) => {
              const locked = !!row.duplicateOf || !!row.error
              return (
                <Table.Tr key={`${row.sheetRow}-${row.languageCode}`} className={classNames({ 'opacity-60': !row.include })}>
                  <Table.Td className="align-top">
                    <Checkbox
                      checked={row.include}
                      disabled={locked}
                      onChange={() => setRow(index, { include: !row.include })}
                    />
                  </Table.Td>
                  <Table.Td className="align-top">
                    <div>{ row.languageName || row.languageLabel }</div>
                    { row.error && <div className="text-negative text-xs">{ row.error }</div> }
                    { row.duplicateOf && (
                      <Link to={`/orders/${row.duplicateOf}/overview`} target="_blank" className="text-xs text-warning">
                        { formatMessage(messages.alreadyIn, { orderId: row.duplicateOf }) }
                      </Link>
                    ) }
                  </Table.Td>
                  <Table.Td className="align-top">{ formatDate(row.dueDate, { day: 'numeric', month: 'short', year: 'numeric' }) }</Table.Td>
                  <Table.Td className="align-top">{ row.status }</Table.Td>
                  <CrewCell member={row.author} disabled={!row.include} onChange={(author) => setRow(index, { author })} />
                  <CrewCell member={row.qc} disabled={!row.include} onChange={(qc) => setRow(index, { qc })} />
                </Table.Tr>
              )
            }) }
          </Table.Tbody>
        </Table.Table>
      </div>
    </Card>
  )
}

const Results = ({ result, onRestart }) => {
  const { formatMessage } = useIntl()
  const [copied, setCopied] = useState(null)

  const copy = (orderId, link) => {
    navigator.clipboard.writeText(link).then(() => setCopied(orderId))
  }

  return (
    <div className="space-y-4">
      { result.created.length > 0 && (
        <Notice state="positive" title={formatMessage(messages.created)}>
          <ul className="space-y-2">
            { result.created.map(({ orderId, title, languages, firstSheetRow }) => {
              const link = `${window.location.origin}/orders/${orderId}`
              return (
                <li key={orderId} className="flex flex-wrap items-center gap-3">
                  <Link to={`/orders/${orderId}/overview`} className="text-primary font-medium">#{ orderId } { title }</Link>
                  <span className="text-gray-500">({ languages })</span>
                  { firstSheetRow && <span>{ formatMessage(messages.pasteLink, { row: firstSheetRow }) }</span> }
                  <Button size="xs" variant="outline" icon={['fas', copied === orderId ? 'check' : 'copy']} onClick={() => copy(orderId, link)}>
                    { formatMessage(copied === orderId ? messages.copied : messages.copyLink) }
                  </Button>
                </li>
              )
            }) }
          </ul>
          <p className="pt-2">{ formatMessage(messages.priceNote) }</p>
        </Notice>
      ) }

      { result.skipped.length > 0 && (
        <Notice state="warning" title={formatMessage(messages.skipped)}>
          <ul>
            { result.skipped.map(({ title, languageCode, sheetRow, reason }, i) => (
              <li key={i}>{ title } · { languageCode }{ sheetRow ? ` · row ${sheetRow}` : '' }: { reason }</li>
            )) }
          </ul>
        </Notice>
      ) }

      { result.errors.length > 0 && (
        <Notice state="negative" title={formatMessage(messages.errors)}>
          <ul>{ result.errors.map((error, i) => <li key={i}>{ error }</li>) }</ul>
        </Notice>
      ) }

      <Button variant="outline" onClick={onRestart}>{ formatMessage(messages.startOver) }</Button>
    </div>
  )
}

const AdSheetImportPage = () => {
  const { formatMessage } = useIntl()
  const fileInput = useRef(null)
  const [loading, setLoading] = useState(false)
  const [titles, setTitles] = useState(null)
  const [warnings, setWarnings] = useState([])
  const [customer, setCustomer] = useState(null)
  const [contact, setContact] = useState(null)
  const [result, setResult] = useState(null)

  const restart = () => {
    setTitles(null)
    setWarnings([])
    setResult(null)
    if (fileInput.current) {
      fileInput.current.value = ''
    }
  }

  const onFile = (file) => {
    if (!file) {
      return
    }
    setLoading(true)
    file.text()
      .then((csv) => post('/rest/v2/order/ad-import-preview', { csv }))
      .then(({ success, data, errors }) => {
        if (!success) {
          toastr.error([].concat(errors).join('\n'))
          return
        }
        setTitles(toEditable(data.titles))
        setWarnings(data.warnings)
      })
      .catch((error) => toastr.error(String(error)))
      .finally(() => setLoading(false))
  }

  const onCustomer = (selected) => {
    setCustomer(selected)
    setContact(null)
    if (+selected?.defaultContact) {
      fetch(`/rest/v2/user/${selected.defaultContact}`)
        .then((response) => response.json())
        .then(({ data }) => data && setContact({ value: data.uid, label: data.name }))
    }
  }

  const loadContacts = debounce((query) => fetch(`/rest/v1/customer/${customer.value}/contacts?search[name]=${encodeURIComponent(query)}`)
    .then((response) => response.json())
    .then((contacts) => contacts.map(({ uid, name }) => ({ value: uid, label: name }))), 250)

  const toImport = (titles || [])
    .map((title) => ({ ...title, rows: title.rows.filter(({ include }) => include) }))
    .filter(({ rows }) => rows.length > 0)

  const onImport = () => {
    setLoading(true)
    const crew = ({ name, selected, cost }) => ({ name, supplierId: selected?.value ?? null, cost })
    post('/rest/v2/order/ad-import', {
      customerId: customer.value,
      contactId: contact?.value,
      titles: toImport.map(({ title, po, rows }) => ({
        title,
        po,
        rows: rows.map((row) => ({
          sheetRow: row.sheetRow,
          languageCode: row.languageCode,
          dueDate: row.dueDate,
          author: crew(row.author),
          qc: crew(row.qc),
        })),
      })),
    })
      .then(({ data, errors }) => {
        if (data) {
          setResult(data)
        } else {
          toastr.error([].concat(errors).join('\n'))
        }
      })
      .catch((error) => toastr.error(String(error)))
      .finally(() => setLoading(false))
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <h1 className="text-2xl font-semibold">{ formatMessage(messages.title) }</h1>

        { result ? (
          <Results result={result} onRestart={restart} />
        ) : !titles ? (
          <Card className="p-4 space-y-4">
            <p className="text-sm text-gray-600 max-w-prose">{ formatMessage(messages.intro) }</p>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={({ target: { files } }) => onFile(files[0])}
            />
            <Button icon={['fas', 'file-csv']} disabled={loading} onClick={() => fileInput.current.click()}>
              { formatMessage(loading ? messages.reading : messages.chooseFile) }
            </Button>
          </Card>
        ) : (
          <>
            { warnings.length > 0 && (
              <Notice state="warning" title={formatMessage(messages.warnings)}>
                <ul>{ warnings.map((warning, i) => <li key={i}>{ warning }</li>) }</ul>
              </Notice>
            ) }

            <Card className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectAsync
                label={formatMessage(messages.customer)}
                description={formatMessage(messages.customerDescription)}
                value={customer}
                onChange={onCustomer}
                cacheOptions
                loadOptions={loadCustomers}
                placeholder={formatMessage(messages.search)}
              />
              <SelectAsync
                key={customer?.value ?? 'none'}
                label={formatMessage(messages.contact)}
                description={formatMessage(messages.contactDescription)}
                value={contact}
                onChange={setContact}
                isDisabled={!customer}
                defaultOptions={!!customer}
                loadOptions={loadContacts}
                placeholder={formatMessage(messages.search)}
              />
            </Card>

            { titles.map((title, index) => (
              <TitleCard
                key={title.key}
                title={title}
                onChange={(changed) => setTitles(titles.map((t, i) => i === index ? changed : t))}
              />
            )) }

            <div className="flex items-center justify-between gap-4 sticky bottom-0 bg-white py-3 border-t border-gray-100">
              <Button variant="text" onClick={restart}>{ formatMessage(messages.startOver) }</Button>
              { toImport.length === 0 ? (
                <span className="text-sm text-gray-500">{ formatMessage(messages.nothingToImport) }</span>
              ) : (
                <Button state="positive" disabled={!customer || loading} onClick={onImport}>
                  { loading ? formatMessage(messages.importing) : formatMessage(messages.importButton, { count: toImport.length }) }
                </Button>
              ) }
            </div>
          </>
        ) }
      </div>
    </div>
  )
}

export default AdSheetImportPage
